import {isNil} from "lodash";
import {Component, Suspense, useMemo} from "react";
import {useTranslation} from "react-i18next";
import useMultiQueryCallerResource from "../../../hooks/useMultiQueryCallerResource";
import {getColorByName} from "../../../utils/ColorUtils";
import {GROUP_FATTENING} from "../../../utils/GroupUtils";
import {
    getTimeOnPigletHouse,
    getTimeOnPorkHouse,
} from "../../../utils/SettingsUtils";
import NotFound from "../../NotFound";
import FitText from "../../basics/fit-text/FitText";
import Tooltip from "../../basics/tooltip/Tooltip";
import CollapsableContainer from "../containers/CollapsableContainer";
import "./_fattening-progress.scss";
import SkeletonRectangle from "../../skeletons/SkeletonRectangle";
import InfoBox from "../../basics/info-box/InfoBox";

function FatteningProgressItem({
    value,
    max,
    color,
    width,
    type,
    tooltipContent,
    showDayText = true,
}) {
    const {t} = useTranslation();

    const colorFillWidth = useMemo(() => {
        if (value > max) {
            return `${(max * 100) / value}%`;
        }
        return `${(value * 100) / max}%`;
    }, [value, max]);

    const errorFill = useMemo(() => {
        if (value <= max) return null;
        return `${((value - max) * 100) / max}%`;
    }, [value, max]);

    return (
        <div style={{width}}>
            <div>
                <FitText>{type}</FitText>
            </div>
            <Tooltip tooltipContent={tooltipContent}>
                <div className="fattening-progress-container">
                    <div
                        style={{backgroundColor: color, width: colorFillWidth}}
                        className="fattening-progress-item"
                    />
                    {errorFill !== null && (
                        <div
                            style={{
                                backgroundColor: getColorByName("error"),
                                width: errorFill,
                            }}
                            className="fattening-progress-item"
                        />
                    )}
                    {showDayText && (
                        <div className="fattening-progress-text">
                            <FitText>{t("Xdays", {count: value})}</FitText>
                        </div>
                    )}
                </div>
            </Tooltip>
        </div>
    );
}

function FatteningProgressTooltipContent({max, value}) {
    const {t} = useTranslation();

    return <div>{t("fatteningDaysExceeded", {count: value - max})}</div>;
}

function FatteningProgressRenderer({resource}) {
    const data = resource.read();

    const {t} = useTranslation();

    const fatteningAge = useMemo(() => {
        if (data.groupFattening.ExecCode === "FAIL")
            return {weanersAge: null, finisherAge: null};
        return data.groupFattening.Data[0];
    }, [data]);

    const {weanersMax, finisherMax} = useMemo(() => {
        return {
            weanersMax: getTimeOnPigletHouse(),
            finisherMax: getTimeOnPorkHouse(),
        };
    }, []);

    const {maxWeaners, maxFinisher} = useMemo(() => {
        const maxWeaners =
            fatteningAge.finisherAge === null
                ? Math.max(weanersMax, fatteningAge.weanersAge)
                : fatteningAge.weanersAge;
        const maxFinisher = Math.max(finisherMax, fatteningAge.finisherAge);
        return {maxWeaners, maxFinisher};
    }, [fatteningAge, finisherMax, weanersMax]);

    const {weanersWidth, finisherWidth} = useMemo(() => {
        // przeliczanie procentowego udzialu warchlaka i tucznika
        if (fatteningAge.weanersAge === null)
            return {weanersWidth: 0, finisherWidth: "100%"};
        const weanersWidth = (maxWeaners * 100) / (maxWeaners + maxFinisher);
        const finisherWidth = 100 - weanersWidth;
        return {
            weanersWidth: `${weanersWidth}%`,
            finisherWidth: `${finisherWidth}%`,
        };
    }, [maxWeaners, maxFinisher, fatteningAge]);

    const activeDay = useMemo(() => {
        return fatteningAge.weanersAge + fatteningAge.finisherAge;
    }, [fatteningAge]);

    const notFoundData =
        isNil(fatteningAge.weanersAge) && isNil(fatteningAge.finisherAge);

    if (notFoundData) {
        return <NotFound />;
    }
    return (
        <div className="fattening-progress">
            {fatteningAge.weanersAge !== null && (
                <FatteningProgressItem
                    value={fatteningAge.weanersAge}
                    max={weanersMax}
                    color={getColorByName("piglets")}
                    width={weanersWidth}
                    type={t("animalTypes.2")}
                    activeDay={activeDay.weaners}
                    tooltipContent={
                        fatteningAge.weanersAge > weanersMax ? (
                            <FatteningProgressTooltipContent
                                max={weanersMax}
                                value={fatteningAge.weanersAge}
                            />
                        ) : null
                    }
                />
            )}
            <FatteningProgressItem
                value={fatteningAge.finisherAge}
                max={finisherMax}
                color={getColorByName("porkers")}
                width={finisherWidth}
                type={t("animalTypes.3")}
                activeDay={activeDay.finisher}
                showDayText={
                    fatteningAge.weanersAge !== null &&
                    fatteningAge.finisherAge !== null
                }
                tooltipContent={
                    fatteningAge.finisherAge > finisherMax ? (
                        <FatteningProgressTooltipContent
                            max={finisherMax}
                            value={fatteningAge.finisherAge}
                        />
                    ) : null
                }
            />
            <div className="fattening-progress-active-day-text">
                {t("dayFromInsertion", {day: activeDay})}
            </div>
        </div>
    );
}

function FatteningProgressSkeleton() {
    const {t} = useTranslation();
    return (
        <div className="fattening-progress">
            <div style={{width: "33%"}}>
                <div>{t("animalTypes.2")}</div>
                <div className="fattening-progress-container">
                    <SkeletonRectangle />
                </div>
            </div>
            <div style={{width: "67%"}}>
                <div>{t("animalTypes.3")}</div>
                <div className="fattening-progress-container">
                    <SkeletonRectangle />
                </div>
            </div>
        </div>
    );
}

class FatteningProgressErrorBoundary extends Component {
    state = {
        hasError: false,
    };

    static getDerivedStateFromError(error) {
        return {hasError: true};
    }

    render() {
        const {children, t} = this.props;
        if (this.state.hasError) {
            return (
                <InfoBox boxColor="error" outlined>
                    <i className="fa-solid fa-exclamation-circle me-2" />
                    <span>{t("newSettings.devices.settingsInfo.noData")}</span>
                </InfoBox>
            );
        }
        return children;
    }
}

export default function FatteningProgress({group}) {
    const {t} = useTranslation();

    const queries = useMemo(() => {
        return [
            {
                SQL: GROUP_FATTENING,
                ClientQueryID: "groupFattening",
                Parameters: [group.AnmGrp],
            },
        ];
    }, [group.AnmGrp]);

    const resource = useMultiQueryCallerResource(queries);

    return (
        <CollapsableContainer.Card
            header={t("chamber.separationCageInfo.fatteningDay")}
            defaultExpanded>
            <FatteningProgressErrorBoundary t={t}>
                <Suspense fallback={<FatteningProgressSkeleton />}>
                    <FatteningProgressRenderer resource={resource} />
                </Suspense>
            </FatteningProgressErrorBoundary>
        </CollapsableContainer.Card>
    );
}
